/*
 * WARNING: LEGACY SESSION EVENTS RESOURCE - ARCHIVAL PURPOSES ONLY
 *
 * This file contains the legacy Session Events implementation and is maintained
 * ONLY for archival and historical data access purposes.
 *
 * IMPORTANT NOTICE:
 * - This is NOT the current events system
 * - Current events system is in: src/resources/events-v2/
 * - This file should NOT be modified for new features
 * - This file should NOT be used for new event creation
 * - This file is kept for data migration and historical access only
 *
 *  PURPOSE:
 * - Access to legacy session event data
 * - Data migration utilities
 * - Historical reporting
 * - Archive management
 *
 *  MAINTENANCE POLICY:
 * - Keep functional for data access
 * - Maintain enterprise-level code quality
 * - All components kept within this file (intentional design)
 * - No external component extraction (archival stability)
 * - Focus on readability and maintainability within constraints
 *
 * This file is intentionally monolithic for archival stability.
 * Do not extract components to external files.
 */

import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  ReferenceField,
  BooleanField,
  usePermissions,
  SearchInput,
  ReferenceInput,
  SelectInput,
} from 'react-admin';
import { truncateString } from '../utils/string';
import EmptyList from '../components/misc/EmptyList';

// Components
const VerificationStatus = ({ isVerified }) => {
  const bgColor = isVerified ? 'green' : 'orange';
  const status = isVerified ? 'Yes' : 'No';

  return (
    <div style={{ backgroundColor: bgColor, textAlign: 'center', borderRadius: 5, padding: 3 }}>
      <span style={{ color: 'white', textAlign: 'center' }}>{status}</span>
    </div>
  );
};

const EventStatus = ({ sessions }) => {
  const bgColor = 'orange';
  const status = 'Upcoming';

  return (
    <div style={{ backgroundColor: bgColor, textAlign: 'center', borderRadius: 5, padding: 3 }}>
      <span style={{ color: 'white', textAlign: 'center' }}>{status}</span>
    </div>
  );
};

const filters = [
  <SearchInput source="name" />,
  <ReferenceInput source="venue" reference="venues" sort={{ field: 'name', order: 'ASC' }} perPage={200}>
    <SelectInput
      label="Venue Event"
      optionText="name"
      // client-side match for consistency with other screens
      // note: SelectInput doesn't support matchSuggestion; this uses full list
    />
  </ReferenceInput>,
  <ReferenceInput source="city" reference="cities">
    <SelectInput optionText={'name.en'} fullWidth />
  </ReferenceInput>,
  <ReferenceInput source="category" reference="categories">
    <SelectInput optionText={'name.en'} fullWidth />
  </ReferenceInput>,
  <SelectInput
    source="isActive"
    choices={[
      { id: true, name: 'Active' },
      { id: false, name: 'Inactive' },
    ]}
    label="Active Filter"
  />,
  <SelectInput
    source="isVerified"
    choices={[
      { id: true, name: 'Verified' },
      { id: false, name: 'Awaiting' },
    ]}
    label="Verified Filter"
  />,
  <ReferenceInput
    source="subcategory"
    reference="subcategories"
    sort={{ field: 'name.en', order: 'ASC' }}
  >
    <SelectInput optionText={'name.en'} fullWidth />
  </ReferenceInput>,
];

const SessionEventList = (props) => {
  const { permissions } = usePermissions();

  if (!permissions) return;

  const isAdmin = permissions.crole === 'admin';
  const isOperator = permissions.crole === 'operator';

  let filter = {};

  if (permissions.crole === 'organisationCompanyManager' && permissions.rid) {
    filter.organisationCompanies = permissions.rid;
  }
  if (permissions.crole === 'venueManager' && permissions.rid) {
    filter.venue = permissions.rid;
  }

  if (permissions.crole !== 'admin' && !permissions.rid) {
    return (
      <EmptyList
        customMessage="You don't the right permission set to view this page. Please contact GişeKıbrıs if you believe this is an error."
        noCreate={true}
      />
    );
  }

  return (
    <List
      {...props}
      empty={<EmptyList entity="sessionEvents" />}
      sort={{ field: 'createdate', order: 'DESC' }}
      filters={filters}
      exporter={false}
      filter={filter}
      title="Session Events"
    >
      <Datagrid bulkActionButtons={false}>
        <FunctionField
          source="name"
          sortable={true}
          sortBy="name"
          sortByOrder="ASC"
          render={(r) => truncateString(r.name, 30)}
        />

        <ReferenceField
          source="venue"
          reference="venues"
          link={false}
          sort={{ field: 'name', order: 'ASC' }}
        >
          <FunctionField source="name" render={(r) => truncateString(r.name, 20)} />
        </ReferenceField>

        <ReferenceField source="category" reference="categories" link={false}>
          <TextField source="name.en" label={'Category'} />
        </ReferenceField>

        <BooleanField source="isActive" label="Active" />
        <FunctionField
          source="isVerified"
          label="Verified"
          render={(r) => <VerificationStatus isVerified={r.isVerified} />}
        />
        <FunctionField
          source="id"
          label="Status"
          render={(r) => <EventStatus sessions={r.sessions} />}
        />

        {!isOperator && (
          <FunctionField
            source="sales"
            label="Sales"
            render={(r) => <a href={`/#/sales?eventID=${r.id}`}>View Sales</a>}
          />
        )}

        {!isOperator && (
          <FunctionField
            source="tickets"
            label="Tickets"
            render={(r) => <a href={`/#/tickets?eventID=${r.id}`}>View Tickets</a>}
          />
        )}
      </Datagrid>
    </List>
  );
};

// Export only the list component
export { SessionEventList };
export default SessionEventList;
